import React, { useMemo } from 'react';
import { CheckIcon, XIcon } from 'lucide-react';
import { useApp } from '../../contexts/AppContext';
import { Card } from '../../components/ui/Card';
import { Badge } from '../../components/ui/Badge';
import { Donut } from '../../components/ui/Donut';
import { StatusBadge } from '../../components/ui/StatusBadge';
import { attendanceSummary } from '../../utils/attendance';
import { eventTitle, eventTypeClass, eventTypeShort, formatDate } from '../../utils/labels';
const donutColor: Record<string, string> = {
  zelena: '#007c69',
  oranzna: '#d97706',
  rdeca: '#e11d48',
  premalo: '#cbd5e1'
};
export function MemberAttendance() {
  const {
    currentMember,
    settings,
    past,
    attendance,
    isExpected
  } = useApp();
  const me = currentMember!;
  const povzetek = attendanceSummary(me, settings);
  const jeGost = me.type === 'gostujoci';
  const zgodovina = useMemo(() => past.filter(e => e.steje && isExpected(e, me.id)).slice(0, 8).map(e => ({
    event: e,
    mark: attendance[e.id]?.[me.id] ?? 'prisoten'
  })), [past, attendance, isExpected, me.id]);
  return <div className="mx-auto max-w-3xl space-y-4" data-id="element-1366" data-mp="22btui1wgmp2d">
      <div data-id="element-1367" data-mp="22cn2t7b2hmmm">
        <h1 className="text-2xl font-semibold text-ink" data-id="element-1368" data-mp="26cdt79wom0qm">Moja prisotnost</h1>
        <p className="mt-0.5 text-sm text-ink-muted" data-id="element-1369" data-mp="26ei62hck6a31">Sezona 2026/27</p>
      </div>

      <Card className="p-5" data-id="element-1370" data-mp="2bd1o46o171q4">
        <div className="flex flex-col items-start gap-5 sm:flex-row sm:items-center" data-id="element-1371" data-mp="2gal4pd87bql8">
          <Donut odstotek={povzetek.status === 'premalo' ? null : povzetek.odstotek} color={donutColor[povzetek.status]} size={112} label="prisotnost" data-id="element-1372" data-mp="2g8h00t2hanjw" />
          <div className="flex-1" data-id="element-1373" data-mp="2u1ymomqgoge5">
            {!jeGost && <StatusBadge status={povzetek.status} odstotek={povzetek.odstotek} showPercent={false} data-id="element-1374" data-mp="2u1gin3xk4xvo" />}
            <p className="mt-2 text-sm text-ink" data-id="element-1375" data-mp="2yyyhg1hv1wpy">
              Prisotni ste bili na <span className="font-semibold" data-id="element-1376" data-mp="340xnjj4zj7ql">{povzetek.prisoten}</span> od{' '}
              <span className="font-semibold" data-id="element-1377" data-mp="13x4w41knqo4a">{povzetek.pricakovan}</span> dogodkov, ki štejejo v
              statistiko.
            </p>
            <p className="mt-1.5 text-[13px] leading-relaxed text-ink-muted" data-id="element-1378" data-mp="z0mqkzhi6lqw">
              {jeGost ? 'Kot gostujoča članica niste vključeni v semafor prisotnosti — vaša udeležba je dobrodošla, a ni obvezna.' : povzetek.status === 'premalo' ? `Semafor se vklopi po ${settings.minDogodkov} štetih dogodkih. Za zdaj prikazujemo nevtralno stanje.` : `Meje: zelena ${settings.pragZelena} % in več, oranžna od ${settings.pragOranzna} % do ${settings.pragZelena} %, rdeča pod ${settings.pragOranzna} %.`}
            </p>
          </div>
        </div>

        {!jeGost && povzetek.status !== 'premalo' && <div className="mt-5" data-id="element-1379" data-mp="29luw272baa8">
            <div className="relative h-2 w-full overflow-hidden rounded-full bg-canvas" data-id="element-1380" data-mp="2an8oefgqavn">
              <div className={`h-full rounded-full ${povzetek.status === 'zelena' ? 'bg-green-500' : povzetek.status === 'oranzna' ? 'bg-amber-500' : 'bg-rose-500'}`} style={{
            width: `${povzetek.odstotek ?? 0}%`
          }} data-id="element-1381" data-mp="3tgonmy2jtomk" />
              <span className="absolute top-0 h-full w-px bg-ink/30" style={{
            left: `${settings.pragZelena}%`
          }} data-id="element-1382" data-mp="244s1k50oz929" />
            </div>
            <div className="mt-1.5 flex justify-between text-[11px] text-ink-muted" data-id="element-1383" data-mp="1v5ohaxpsgnm6">
              <span data-id="element-1384" data-mp="1g8npku7lksnp">0 %</span>
              <span data-id="element-1385" data-mp="1g9fl65172qdz">prag {settings.pragZelena} %</span>
              <span data-id="element-1386" data-mp="1l78ngmzdn08z">100 %</span>
            </div>
          </div>}
      </Card>

      <Card className="p-5" data-id="element-1387" data-mp="1l91y0dd424m9">
        <h2 className="text-base font-semibold text-ink" data-id="element-1388" data-mp="3alnddam4st62">Zadnji dogodki</h2>
        <ul className="mt-3 divide-y divide-line" data-id="element-1389" data-mp="3fl9n3a2r3ra4">
          {zgodovina.map(({
          event,
          mark
        }) => <li key={event.id} className="flex items-center gap-3 py-3" data-id="element-1390" data-mp="1fj0b5gw99eoj">
              <span className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full ${mark === 'prisoten' ? 'bg-green-50 text-green-600' : 'bg-rose-50 text-rose-600'}`} data-id="element-1391" data-mp="1fgwbjb5e31u2">
                {mark === 'prisoten' ? <CheckIcon className="h-4 w-4" data-id="element-1392" data-mp="11ifvgtlmzyz9" /> : <XIcon className="h-4 w-4" data-id="element-1393" data-mp="11lljs6mfywtx" />}
              </span>
              <span className="min-w-0 flex-1" data-id="element-1394" data-mp="wkxubpdv2o1t">
                <span className="block truncate text-sm font-medium text-ink" data-id="element-1395" data-mp="rtfuj4nkqtif">{eventTitle(event)}</span>
                <span className="block text-[13px] text-ink-muted" data-id="element-1396" data-mp="nrkstz1qee44">{formatDate(event.datum)}</span>
              </span>
              <Badge className={eventTypeClass[event.type]} data-id="element-1397" data-mp="itizmlalcyex">{eventTypeShort[event.type]}</Badge>
            </li>)}
          {zgodovina.length === 0 && <li className="py-6 text-center text-[13px] text-ink-muted" data-id="element-1398" data-mp="dvhjo98gpf7o">Zaenkrat ni zabeleženih dogodkov.</li>}
        </ul>
      </Card>
    </div>;
}